import { useCallback, useEffect, useState } from 'react';
import {
    getNotasClinicas,
    getNotaClinicaById,
    buscarNotas,
    getEstatisticasNotas
} from '../../api/api';

/**
 * Hook para buscar notas clínicas do paciente com filtros
 * @param {number} pacienteId - ID do paciente
 * @param {object} options - Opções de filtro { tipo, categoria, rascunho, startDate, endDate, usuarioId, search, tags }
 */
export function useNotasClinicas(pacienteId, options = {}) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!pacienteId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getNotasClinicas(pacienteId, options);
            setData(response?.data?.data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Erro ao carregar notas clínicas.');
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId, JSON.stringify(options)]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch
    };
}

/**
 * Hook para buscar uma nota clínica específica
 * @param {number} pacienteId - ID do paciente
 * @param {number} notaId - ID da nota
 */
export function useNotaClinica(pacienteId, notaId) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!pacienteId || !notaId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getNotaClinicaById(pacienteId, notaId);
            setData(response?.data?.data || null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Erro ao carregar nota clínica.');
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId, notaId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch
    };
}

/**
 * Hook para buscar notas (full-text search)
 * @param {number} pacienteId - ID do paciente
 * @param {string} termo - Termo de busca
 * @param {object} options - Opções { limit }
 */
export function useBuscarNotas(pacienteId, termo, options = {}) {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const buscar = useCallback(async () => {
        if (!pacienteId || !termo || termo.trim().length < 2) {
            setData([]);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await buscarNotas(pacienteId, termo.trim(), options);
            setData(response?.data?.data || []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Erro ao buscar notas.');
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId, termo, JSON.stringify(options)]);

    return {
        data,
        isLoading,
        error,
        buscar
    };
}

/**
 * Hook para buscar estatísticas das notas
 * @param {number} pacienteId - ID do paciente
 */
export function useEstatisticasNotas(pacienteId) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        if (!pacienteId) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await getEstatisticasNotas(pacienteId);
            setData(response?.data?.data || null);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Erro ao carregar estatísticas.');
        } finally {
            setIsLoading(false);
        }
    }, [pacienteId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refetch = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        isLoading,
        error,
        refetch
    };
}

export default useNotasClinicas;

